// FeaturedBanner — bandeau défilant en haut du site avec les produits mis en avant
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API, useLang, useSettings, fmtDzd, cldUrl } from "../lib/providers";
import { Star, ArrowRight } from "lucide-react";

export default function FeaturedBanner() {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const { lang } = useLang();
  const { settings } = useSettings();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products/featured`)
      .then((r) => setItems(r.data?.data || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  if (!items || items.length === 0) return null;
  const current = items[index];
  const rate = settings.exchange_rate || 280;
  const dzd = current.price_dzd > 0 ? current.price_dzd : (current.price_eur || 0) * rate;
  const finalPrice = current.promo_active && current.promo_price_dzd > 0 ? current.promo_price_dzd : dzd;
  const promoPct = current.promo_active && current.promo_price_dzd > 0 && current.promo_price_dzd < dzd
    ? Math.round(((dzd - current.promo_price_dzd) / dzd) * 100) : 0;
  const promoLabel = lang === "ar" ? (current.promo_label_ar || current.promo_label_fr) : current.promo_label_fr;

  return (
    <section
      className="relative w-full bg-gradient-to-r from-neon/10 via-black to-clay/10 border-y border-neon/20 overflow-hidden"
      data-testid="featured-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-5 sm:py-6">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4 text-neon" fill="currentColor" />
          <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-neon" data-testid="featured-banner-title">
            {lang === "ar" ? "🌟 مختاراتنا" : "🌟 Nos coups de cœur"}
          </h2>
          <span className="text-[10px] text-white/40 font-mono ms-2">{index + 1}/{items.length}</span>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-4 items-center cursor-pointer group"
          onClick={() => navigate(`/produit/${current.id}`)}
          data-testid={`featured-item-${current.id}`}
        >
          {current.image_url && (
            <div className="w-24 h-24 sm:w-28 sm:h-28 bg-black/40 overflow-hidden border border-white/10 group-hover:border-neon transition-colors mx-auto sm:mx-0">
              <img
                src={cldUrl(current.image_url, { w: 240 })}
                alt={current.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                loading="lazy"
              />
            </div>
          )}
          <div className="flex-1 min-w-0 text-center sm:text-start">
            <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{current.brand || "—"}</div>
            <h3 className="font-heading font-black text-lg sm:text-xl uppercase leading-tight line-clamp-2">
              {current.name}
            </h3>
            <div className="mt-2 flex items-center gap-3 flex-wrap justify-center sm:justify-start">
              {promoPct > 0 && (
                <>
                  <span className="text-xs text-white/40 line-through font-mono">{fmtDzd(dzd)}</span>
                  <span className="text-[10px] font-black uppercase bg-clay text-white px-1.5 py-0.5">
                    {promoLabel || `-${promoPct}%`}
                  </span>
                </>
              )}
              <span className="font-heading font-black text-xl text-neon" data-testid={`featured-price-${current.id}`}>
                {fmtDzd(finalPrice)}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-center sm:justify-end gap-2 text-neon font-black text-xs uppercase tracking-widest group-hover:gap-3 transition-all">
            {lang === "ar" ? "اطلع" : "Voir"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>

        {items.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-4">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                className={`h-1.5 transition-all ${i === index ? "w-8 bg-neon" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`featured-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
